import { track } from '../analytics/events.js';
import { publicOrigin } from './publicOrigin.js';

// Sharing an area: its link is the area page, /omrade/<RegSO code>/<name as a slug> (src/area/AreaPage.js);
// only the code is read back, the slug just makes the link readable. A shared link unfurls with the area's
// own card (design A1, drawn by the backend's og_share.py; the Pages middleware sets the tags).
//
// The area card (tooltipData.js) is a plain HTML string, so its Dela button is handled by one install-once
// click listener on the document (capture phase, so a Leaflet popup's stopped clicks still reach it). Phones
// get the share sheet; elsewhere the link is copied and the button says so for a moment.

const COPIED_MS = 2200;

export const areaSlug = (name) => String(name || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

export const areaPagePath = (code, name) => {
    const slug = areaSlug(name);
    return `/omrade/${encodeURIComponent(code)}${slug ? `/${slug}` : ''}`;
};

// Always the public site's address, also when shared from a dev server (publicOrigin.js).
export const areaShareUrl = (code, name) => `${publicOrigin()}${areaPagePath(code, name)}`;

const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (error) {
        try {
            const field = document.createElement('textarea');
            field.value = text;
            field.setAttribute('readonly', '');
            field.style.position = 'fixed';
            field.style.opacity = '0';
            document.body.appendChild(field);
            field.select();
            const copied = document.execCommand('copy');
            field.remove();
            return copied;
        } catch (fallbackError) {
            return false;
        }
    }
};

const prefersShareSheet = () => (
    typeof navigator !== 'undefined' && typeof navigator.share === 'function'
    && typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches
);

// Shares the area: the share sheet on phones, else copies the link. Resolves 'shared' | 'copied' | 'aborted'
// | 'failed'.
export const shareArea = async ({ code, name, source }) => {
    const url = areaShareUrl(code, name);
    if (prefersShareSheet()) {
        try {
            await navigator.share({ title: name, url });
            track('area.share', { method: 'native', source, regso: code });
            return 'shared';
        } catch (error) {
            if (error && error.name === 'AbortError') return 'aborted';
        }
    }
    const copied = await copyText(url);
    if (copied) track('area.share', { method: 'copy', source, regso: code });
    return copied ? 'copied' : 'failed';
};

// A place in an area (the map's "Gå till" pin): the area's page with the place in the query, which puts the
// address on top of the page and the pin on its map (AreaPage.js), and unfurls with the place's own card.
export const placeQuery = ({ lat, lng, name }) => (
    `plats=${Number(lat).toFixed(5)},${Number(lng).toFixed(5)}&namn=${encodeURIComponent(String(name || '').trim())}`
);
export const placeShareUrl = (code, areaName, place) => `${areaShareUrl(code, areaName)}?${placeQuery(place)}`;

// Reads ?plats=<lat>,<lng>&namn=<name> back: { lat, lng, name } or null.
export const readPlaceQuery = (search) => {
    const params = new URLSearchParams(search || '');
    const [lat, lng] = String(params.get('plats') || '').split(',').map(Number);
    const name = String(params.get('namn') || '').replace(/\s+/g, ' ').trim().slice(0, 80);
    if (!name || !Number.isFinite(lat) || !Number.isFinite(lng) || lat < 55 || lat > 69.5 || lng < 10.5 || lng > 24.5) return null;
    return { lat, lng, name };
};

// Shares a place like shareArea shares an area.
export const sharePlace = async ({ code, areaName, place, source }) => {
    const url = placeShareUrl(code, areaName, place);
    if (prefersShareSheet()) {
        try {
            await navigator.share({ title: place.name, url });
            track('place.share', { method: 'native', source, regso: code });
            return 'shared';
        } catch (error) {
            if (error && error.name === 'AbortError') return 'aborted';
        }
    }
    const copied = await copyText(url);
    if (copied) track('place.share', { method: 'copy', source, regso: code });
    return copied ? 'copied' : 'failed';
};

// A comparison of areas (the comparison card in the views, CompareCard.js): its page, /jamfor/<code>,<code>…
// (src/area/ComparePage.js), which unfurls with the areas listed with their scores. At most MAX_COMPARED areas
// (the backend's og_share.py takes as many).
export const MAX_COMPARED = 8;
export const comparePath = (codes) => `/jamfor/${codes.slice(0, MAX_COMPARED).map(encodeURIComponent).join(',')}`;
export const compareShareUrl = (codes) => `${publicOrigin()}${comparePath(codes)}`;

export const shareComparison = async ({ codes, names, source }) => {
    const url = compareShareUrl(codes);
    const meta = { source, count: Math.min(codes.length, MAX_COMPARED) };
    if (prefersShareSheet()) {
        try {
            await navigator.share({ title: (names || []).slice(0, MAX_COMPARED).join(', '), url });
            track('compare.share', { method: 'native', ...meta });
            return 'shared';
        } catch (error) {
            if (error && error.name === 'AbortError') return 'aborted';
        }
    }
    const copied = await copyText(url);
    if (copied) track('compare.share', { method: 'copy', ...meta });
    return copied ? 'copied' : 'failed';
};

const SHARE_ICON = '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 15V3"/><path d="M7.5 7.5L12 3l4.5 4.5"/><path d="M5 12v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-7"/></svg>';

const escapeAttr = (value) => String(value || '').replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[ch]));

// The Dela button for an area card's heading (design A of the "Dela områden" canvas, 2026-10-31): the word on
// it, in the card's top row, so it says what it does.
export const areaShareButton = (code, name, swedish) => (code
    ? `<button type="button" class="area-card-share" data-area-share="${escapeAttr(code)}" data-area-name="${escapeAttr(name)}" data-copied-label="${swedish ? 'Länk kopierad! Klistra in i en chatt' : 'Link copied! Paste it in a chat'}" aria-label="${swedish ? 'Dela området' : 'Share the area'}">${SHARE_ICON}<span>${swedish ? 'Dela' : 'Share'}</span></button>`
    : '');

let installed = false;
export const installAreaShareHandler = () => {
    if (installed || typeof document === 'undefined') return;
    installed = true;
    document.addEventListener('click', (event) => {
        const button = event.target instanceof Element ? event.target.closest('.area-card-share') : null;
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        shareArea({
            code: button.getAttribute('data-area-share'),
            name: button.getAttribute('data-area-name'),
            source: 'card',
        }).then((result) => {
            if (result !== 'copied') return;
            button.classList.add('is-copied');
            button.setAttribute('data-tip', button.getAttribute('data-copied-label') || '');
            clearTimeout(button.__copiedTimer);
            button.__copiedTimer = setTimeout(() => {
                button.classList.remove('is-copied');
                button.removeAttribute('data-tip');
            }, COPIED_MS);
        });
    }, true);
};
